/**
 * **갈림길**(Todo5 6단계). 관문을 넘어 새 구간(6–9, 11–14, …)에 들 때 한 번 묻는다: 어느 길로 갈까(`Route`). 길이 그
 * 구간 네 깊이의 땅을 정한다. 출정을 구간 첫 깊이(6·11·…)에서 시작해도 첫 판 앞에서 같은 자리(`openBattle`)로 온다.
 *
 * 길마다 네 깊이의 특성과 적 수를 모두 보인다 — 출정 시드가 정하므로 미리 알 수 있고, 네 판을 버틸 길을 편성에 맞춰
 * 고르는 것이 이 선택의 뜻이다. 폰에서 잘못 눌러 바로 나서지 않게 고르고 나서 확정한다(전투 준비 화면과 같은 꼴).
 *
 * 깊이는 **표**로 보인다: 깊이 · 적 수(막대) · 특성(갈래마다 색이 다른 딱지) · 전과 가산의 합. 처음에는 깊이마다
 * "깊이 31 · 적 80명 · 산길 +5% · 중장보병 +15%" 한 줄 글이었고, 길의 설명 두 줄과 위의 안내 두 문단까지 글만 스무 줄
 * 가까이라 두 길을 견줄 수가 없었다(플레이한 사람: "글자가 너무 많은데 정보가 한눈에 캐치되도록 개선해줘"). 견줄 것은
 * 수(적이 몇인가, 전과가 얼마인가)와 갈래(땅인가 적인가)라, 수는 자리를 맞춰 세로로 읽히게 하고 갈래는 색으로 가른다.
 * 전과 가산은 한 판의 합을 오른쪽에 적고, 특성마다의 몫은 딱지 안에 작은 수로 남긴다 — 처음에는 합만 적었더니 어느
 * 특성이 큰지(좁은 협곡 +50인가 궁수대 +15인가) 알 수 없어 편성에 맞는 길을 고르기 어려웠다(QA가 짚었다).
 *
 * 화면 읽기에는 칸의 머리줄이 닿지 않는다(단추 안의 격자는 표가 아니다) — 수마다 숨긴 말("깊이", "적 … 명", "전과
 * 가산")을 붙여 읽는 글이 옛 한 줄 글과 같게 한다. 처음에는 머리줄만 두어 "31 73 +20%"로 읽혔다(리뷰·QA가 짚었다).
 */
import { ROUTES, TRAIT_BY_ID, Trait, TraitKind, forecastTraits, gateStoryName, segmentDepths, traitMeritPct, traitsForDepth } from '../sim/traits.ts';
import type { Route } from '../sim/traits.ts';
import { depthRoster } from '../sim/troops.ts';
import { lateFoes } from '../sim/scenario.ts';
import type { Army } from '../meta/army.ts';
import type { RunState } from '../meta/run.ts';
import { enemyCultureFor, scouted } from '../meta/run.ts';
import { button, clear, el } from './dom.ts';

/** 표의 한 줄(한 깊이). 화면과 시험이 같은 것을 본다. */
export interface DepthRow {
  depth: number;
  /** 판을 열 때 서 있는 적. */
  foes: number;
  /** 판 도중에 더 오는 적(매복·원군). 안개에 가려 있으면 0 — 모르는 것은 적지 않는다. */
  more: number;
  /** 안개가 끼었고 척후가 보지 않았다: 특성은 안개만 보이고 전과 가산은 모른다. */
  veiled: boolean;
  /** 보이는 특성(예보 순서 그대로). `pct`는 그 특성의 전과 가산 — 가려 있으면 null. */
  traits: { name: string; kind: TraitKind; pct: number | null }[];
  /** 전과 가산의 합(백분율). 가려 있으면 null. */
  meritPct: number | null;
}

export function depthRow(army: Army, run: RunState, depth: number, route: Route): DepthRow {
  const traits = traitsForDepth(run.seed, depth, route);
  const roster = depthRoster(enemyCultureFor(army.culture, depth), depth, traits);
  // 안개(`Trait.Fog`)가 낀 깊이는 척후가 보지 않았으면 특성과 더 오는 적을 가린다(`forecastTraits`).
  const veiled = traits.includes(Trait.Fog) && !scouted(run, depth);
  const late = lateFoes(roster, traits);
  const shown: DepthRow['traits'] = [];
  for (const t of forecastTraits(traits, !veiled)) {
    const def = TRAIT_BY_ID.get(t);
    if (def) shown.push({ name: def.name, kind: def.kind, pct: veiled ? null : def.meritPct });
  }
  return {
    depth,
    foes: roster.reduce((n, e) => n + e.count, 0),
    more: veiled ? 0 : late.ambush + late.reinforce,
    veiled,
    traits: shown,
    meritPct: veiled ? null : traitMeritPct(traits),
  };
}

/** 갈래의 이름 — 딱지의 색이 무엇을 뜻하는지 범례가 적는다. */
const KIND_NAMES: Record<TraitKind, string> = {
  // 길이 정하는 것은 땅뿐이다 — 적 편성과 날씨는 어느 길이든 같은 규칙으로 온다. 범례가 그것을 말한다.
  [TraitKind.Land]: '땅 — 길이 정한다',
  [TraitKind.Foe]: '적 편성',
  [TraitKind.Cond]: '날씨·전황',
  [TraitKind.Tide]: '적의 세기',
};

function meritText(pct: number | null): string {
  return pct === null ? '+?%' : `+${pct}%`;
}

/** 화면 읽기만 듣는 말. */
function sr(text: string): HTMLElement {
  return el('span', { class: 'sr-only' }, text);
}

/** 한 길의 끝줄: 적의 합(더 오는 적까지)과 전과 가산의 평균(가려진 판은 뺀다 — 모두 가려졌으면 null). */
export function routeSummary(rows: readonly DepthRow[]): { foes: number; avgPct: number | null; veiled: number } {
  const known = rows.filter((r) => r.meritPct !== null);
  return {
    foes: rows.reduce((n, r) => n + r.foes + r.more, 0),
    avgPct: known.length > 0 ? Math.round(known.reduce((n, r) => n + (r.meritPct ?? 0), 0) / known.length) : null,
    veiled: rows.length - known.length,
  };
}

/** 막대의 기준: 두 길을 통틀어 한 판에 가장 많은 적(더 오는 적까지). 길마다 따로 잡으면 길끼리 견줄 수 없다. */
export function barTop(tables: readonly (readonly DepthRow[])[]): number {
  return Math.max(0, ...tables.flat().map((r) => r.foes + r.more));
}

/**
 * 한 길의 표. 단추(`pick-card`) 안이라 모두 `span`이다. 적 수의 막대는 두 길의 모든 줄에서 가장 많은 수(`top`)에 견준
 * 길이라, 길끼리도 줄끼리도 눈으로 견줄 수 있다. 끝줄은 네 판의 합(적)과 평균(전과)이다.
 */
function routeTable(rows: readonly DepthRow[], top: number): HTMLElement {
  const head = el(
    'span',
    { class: 'route-row route-head', 'aria-hidden': 'true' },
    el('span', {}, '깊이'),
    el('span', {}, '적'),
    el('span', {}, '전장'),
    el('span', { class: 'route-merit' }, '전과'),
  );
  const lines = rows.map((r) => {
    const all = r.foes + r.more;
    const bar = el('i', {});
    bar.style.width = `${top > 0 ? Math.max(4, Math.round((all * 100) / top)) : 0}%`;
    return el(
      'span',
      { class: 'route-row' },
      el('span', { class: 'route-depth' }, sr('깊이 '), String(r.depth)),
      el(
        'span',
        { class: 'route-foes' },
        el(
          'span',
          {},
          sr(' 적 '),
          `${r.foes}`,
          sr('명'),
          r.more > 0 ? el('small', { title: '판 도중에 더 오는 적' }, sr(', 더 오는 적 '), `+${r.more}`) : null,
        ),
        el('span', { class: 'route-bar' }, bar),
      ),
      el(
        'span',
        { class: 'route-traits' },
        ...r.traits.map((t) =>
          el('span', { class: `trait-tag ${t.kind}` }, sr(', '), t.name, t.pct !== null && t.pct > 0 ? el('small', {}, ` +${t.pct}`, sr('%')) : null),
        ),
      ),
      el('span', { class: 'route-merit' }, sr(', 전과 가산 '), meritText(r.meritPct), sr('. ')),
    );
  });
  const total = routeSummary(rows);
  const sum = el(
    'span',
    { class: 'route-row route-sum' },
    el('span', {}, '합계'),
    el('span', { class: 'route-foes' }, el('span', {}, sr(' 적 '), `${total.foes}`, sr('명, '))),
    el('span', { class: 'route-avg' }, total.veiled > 0 ? '안개 낀 판을 뺀 전과 평균' : '전과 평균'),
    el('span', { class: 'route-merit' }, sr(' '), meritText(total.avgPct)),
  );
  return el('span', { class: 'route-days' }, head, ...lines, sum);
}

/**
 * `onBack`: 이전 화면으로(편성 또는 지난 판의 결과, `backFromCrossroads`). 처음에는 길을 고르는 것 말고 나갈 길이
 * 없어, 편성을 잘못 짰거나 결과를 다시 보고 싶어도 이 화면에서 갇혔다(사용자가 청했다).
 */
export function renderCrossroads(
  root: HTMLElement,
  army: Army,
  run: RunState,
  onDone: (route: Route) => void,
  onBack: () => void,
): void {
  const depths = segmentDepths(run.depth).filter((d) => d >= run.depth);
  let picked: Route | null = null;
  let focusKey = '';

  const list = el('div', { class: 'pick-list', role: 'group', 'aria-label': '갈 길' });
  const count = el('small', { 'aria-live': 'polite' });
  const go = button('', () => {
    if (picked) onDone(picked);
  }, 'danger');
  go.append('이 길로 나선다', count);

  // 표는 고르는 것과 상관없이 같다 — 한 번만 셈한다. 막대의 기준은 두 길을 통틀어 가장 많은 적이다.
  const tables = ROUTES.map((r) => depths.map((d) => depthRow(army, run, d, r.id)));
  const top = barTop(tables);
  const kinds = new Set(tables.flat().flatMap((r) => r.traits.map((t) => t.kind)));

  const draw = (): void => {
    let focusCard: HTMLButtonElement | null = null;
    clear(list);
    for (const [k, r] of ROUTES.entries()) {
      const on = picked === r.id;
      const card = button('', () => {
        picked = r.id;
        focusKey = r.id;
        draw();
      }, `pick-card${on ? ' on' : ''}`);
      if (focusKey === r.id) focusCard = card;
      card.setAttribute('aria-pressed', String(on));
      card.append(
        el('b', {}, `${r.name} ${r.hanja}`),
        // 길의 설명은 두 줄: 누구의 길인가, 어떤 땅인가(작게). 땅 설명은 처음에 풍선말로만 남겼는데 폰에는 풍선말이 없어
        // "들길에는 적 기마대가 잦다"를 볼 길이 없었고, 고른 길에만 보였더니 길을 바꿀 때마다 카드가 한 줄씩 늘고 줄어
        // 화면이 출렁였다(리뷰·QA가 짚었다). 두 길에 늘 적는다 — 견주려면 둘 다 보여야 한다.
        el('span', { class: 'pick-lead' }, r.hint),
        el('span', { class: 'pick-lead route-about' }, r.summary),
        routeTable(tables[k], top),
      );
      list.append(card);
    }
    const way = ROUTES.find((r) => r.id === picked);
    count.textContent = way ? way.name : '길을 고르라';
    go.disabled = picked === null;
    focusCard?.focus();
  };
  draw();

  const last = depths[depths.length - 1];
  const gate = gateStoryName(last + 1);
  clear(root);
  root.append(
    el(
      'div',
      { class: 'result-wrap' },
      el(
        'header',
        { class: 'result-head' },
        el('span', { class: 'eyebrow' }, `깊이 ${run.depth} · 갈림길`),
        el('h1', {}, '어느 길로 갈까'),
      ),
      el(
        'section',
        { class: 'panel pick-note' },
        el(
          'p',
          {},
          depths.length > 1 ? `깊이 ${depths[0]}–${last}` : `깊이 ${last}`,
          ` · 다음 관문${gate ? `(${gate})` : ''}까지 못 바꾼다`,
        ),
        // 범례: 이 화면에 나온 갈래만. 색만으로 가르지 않게 이름을 적는다.
        el(
          'p',
          { class: 'route-legend' },
          ...Object.values(TraitKind)
            .filter((kd) => kinds.has(kd))
            .map((kd) => el('span', { class: `trait-tag ${kd}` }, KIND_NAMES[kd])),
        ),
      ),
      list,
      el(
        'div',
        { class: 'result-actions' },
        // 출정 첫 판이면 편성으로(낸 급료를 돌려받는다), 진군해 왔으면 지난 판의 결과로.
        button(run.battles === 0 ? '편성으로 돌아간다' : '결과로 돌아간다', onBack),
        go,
      ),
    ),
  );
}
